/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { ViewModel } from '../../../../core/viewmodel/ViewModel';
import { Pages } from '../../../../navigation/Pages';
import { useLocation } from 'react-router-dom';
import { StoreProvider } from '../../../../stores/StoreProvider';
import * as H from 'history';
import { ChatStore } from '../../../../stores/components/ChatStore';
import { CourseStore } from '../../../../stores/components/CourseStore';
import { AuthStore } from '../../../../stores/application/AuthStore';
import { CourseService } from '../../../../services/CourseService';
import { SnackbarStore } from '../../../../stores/application/SnackbarStore';
import { socket } from '../../../../config/web-sockets';
import { role } from '../../../../consts/chat';
import { Message } from '../../../../types/Message';
import { getIsoDateWithoutTime, getRoomId } from '../../../../utils/chat';
import { ChatService } from '../../../../services/ChatService';

export class CourseNavigationViewModel extends ViewModel {
	private pathName = useLocation().pathname;
	private query = new URLSearchParams(useLocation().search);

	private readonly authStore: AuthStore;
	private readonly snackbarStore: SnackbarStore;
	private readonly chatStore: ChatStore;
	private readonly courseStore: CourseStore;
	private readonly courseService: CourseService;
	private readonly chatService: ChatService;
	private readonly courseId: string;

	public isReloading = false;

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.authStore = storeProvider.authStore;
		this.snackbarStore = storeProvider.snackbarStore;
		this.chatStore = storeProvider.chatStore;
		this.courseStore = storeProvider.courseStore;
		this.courseService = storeProvider.courseService;
		this.chatService = storeProvider.chatService;
		this.courseId = this.pathName.substring(this.pathName.indexOf('/', 1) + 1, this.pathName.lastIndexOf('/'));
		this.enableCourse();
	}

	public async enableCourse(): Promise<void> {
		this.queryCourseName();
		await this.queryStudents();
		await this.queryMessages();
		this.joinChat();
		this.reactOnMessage();
	}

	private queryStudents(): Promise<void> {
		return this.courseService
			.getCourseStudents(this.authStore.getAuthToken(), this.courseId)
			.then((studentArray) => {
				this.chatStore.setStudents(studentArray);
				this.chatStore.setAreStudentsBeingFetched(false);
			})
			.catch(() => {
				this.snackbarStore.showError('chat.fetch_students_error');
			});
	}

	private joinChat(): void {
		const userId = this.authStore.getUserId();

		socket.emit('join', { role: role, userId, room: this.courseId }, async (err: Error) => {
			if (err) {
				this.snackbarStore.showError('chat.join_error');
			}
		});

		this.chatStore.getStudents().forEach((student) => {
			const room = getRoomId(this.courseId, this.authStore, student.id);

			socket.emit('join', { role: role, userId, room }, async (err: Error) => {
				if (err) {
					this.snackbarStore.showError('chat.join_error');
				}
			});
		});
	}

	private async queryMessages(): Promise<void> {
		const authToken = this.authStore.getAuthToken();
		const students = this.chatStore.getStudents();

		const messageArray = await this.chatService.getMessages(authToken, this.courseId);
		this.saveMessages(messageArray, this.courseId);
		this.chatStore.setAreMessagesBeingFetched(this.courseId, false);

		for (const student of students) {
			const room = getRoomId(this.courseId, this.authStore, student.id);

			const messageArray = await this.chatService.getMessages(authToken, room);
			this.saveMessages(messageArray, room);
			this.chatStore.setAreMessagesBeingFetched(room, false);
		}

		this.setMessagesAsRead();
	}

	private saveMessages(messageArray: Message[], room: string): void {
		for (let i = 0; i < messageArray.length; i++) {
			if (i === 0) {
				messageArray[i].newDay = true;
			} else {
				messageArray[i].newDay =
					getIsoDateWithoutTime(messageArray[i - 1].identifier) < getIsoDateWithoutTime(messageArray[i].identifier);
			}
		}

		this.chatStore.setMessages(room, messageArray);
		if (this.chatStore.getUnreadMessagesAmount(room) > 0) {
			return;
		}
		messageArray.forEach((message) => {
			if (!message.seenByTeacher) {
				this.chatStore.addUnreadMessage(message.identifier, room);
			}
		});
	}

	private setMessagesAsRead(): void {
		if (this.pathName.includes('chat')) {
			const chatId = this.query.get('id') ?? this.courseId;
			const authToken = this.authStore.getAuthToken();

			this.chatStore.resetUnreadMessages(chatId);
			this.chatService.setMessagesAsRead(authToken, chatId).catch(() => {
				this.snackbarStore.showError('chat.set_as_read_error');
			});
		}
	}

	private reactOnMessage(): void {
		socket.on('message', (message: Message) => {
			if (
				this.chatStore.getActiveChat() !== message.room &&
				!this.chatStore.getUnreadMessages().includes(message.identifier)
			) {
				this.chatStore.addUnreadMessage(message.identifier, message.room);
			}
		});
	}

	public queryCourseName(): void {
		const authToken = this.authStore.getAuthToken();

		this.courseService
			.getCourse(authToken, this.courseId)
			.then((course) => {
				this.courseStore.setCourseName(course.name);
			})
			.catch(() => {
				this.snackbarStore.showError('courses.fetch_name_error');
			});
	}

	public navbarItems: string[] = [
		'courses.management_menu.chat',
		'courses.management_menu.modules',
		'courses.management_menu.students',
		'courses.management_menu.entries',
	];

	public navigateToSubPage(index: number): void {
		this.navigateTo(`/courses/${this.courseId}${this.getSelectedPage(index)}`);
	}

	public getActiveMenu(menuItem: string): boolean {
		const location = useLocation();
		const pathName = location.pathname.substring(location.pathname.lastIndexOf('/'));

		switch (pathName) {
			case Pages.CHAT: {
				if (menuItem === this.navbarItems[0]) {
					return true;
				}
				break;
			}

			case Pages.MODULES: {
				if (menuItem === this.navbarItems[1]) {
					return true;
				}
				break;
			}

			case Pages.STUDENT_MANAGEMENT: {
				if (menuItem === this.navbarItems[2]) {
					return true;
				}
				break;
			}

			case Pages.ENTRIES: {
				if (menuItem === this.navbarItems[3]) {
					return true;
				}
				break;
			}
		}

		return false;
	}

	public getSelectedPage(index: number): string {
		switch (index) {
			case 0:
				return Pages.CHAT;

			case 1:
				return Pages.MODULES;

			case 2:
				return Pages.STUDENT_MANAGEMENT;

			case 3:
				return Pages.ENTRIES;
		}

		return '/';
	}

	public getUnreadMessages(): number {
		let unreadMessages = 0;
		const unreadMessagesArray = this.chatStore.getAllUnreadMessageAmounts();

		for (const key in unreadMessagesArray) {
			unreadMessages += unreadMessagesArray[key];
		}

		return unreadMessages;
	}

	public get reload(): boolean {
		return this.chatStore.reload;
	}
}
